import React, { useState, useMemo } from "react"
import { Card, Col, Row, Statistic, Input, Select, Button, Modal, Typography, Tooltip as AntdTooltip, } from "antd"
import { ArrowLeftOutlined, SearchOutlined, UserOutlined, FileDoneOutlined, FileExclamationOutlined, FolderOpenOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LabelList, Legend } from "recharts"
import DetalleProyectoModal from "./DetalleProyectoModal"
import styles from "./ResumenMetricas.module.css";

const { Title } = Typography;

const parsePeriodoToDate = (periodo) => {
    const clean = periodo
        .toLowerCase()
        .replace(/-/g, " ")
        .replace(/\s+/g, " ")
        .trim(); // Normaliza: guiones, espacios, etc.

    const match = clean.match(/(\w+)\s*(\w+)?\s*(\d{4})$/); // captura mes1 mes2 año

    if (match) {
        const [, mes1, , año] = match;
        const meses = {
            enero: 0,
            febrero: 1,
            marzo: 2,
            abril: 3,
            mayo: 4,
            junio: 5,
            julio: 6,
            agosto: 7,
            septiembre: 8,
            octubre: 9,
            noviembre: 10,
            diciembre: 11,
        };
        return new Date(parseInt(año), meses[mes1.toLowerCase()] || 0, 1);
    }

    return new Date(0); // si no se puede parsear, lo manda al inicio
};


export default function VistaDistribucion({ data, onBack }) {
    const [proyectoSeleccionado, setProyectoSeleccionado] = useState(null)
    const [modalAbierto, setModalAbierto] = useState(false)
    const [busquedaNombreCedula, setBusquedaNombreCedula] = useState("")
    const [filtroMunicipio, setFiltroMunicipio] = useState("todos")
    const [filtroDepto, setFiltroDepto] = useState("todos")
    const [filtroProyecto, setFiltroProyecto] = useState("todos")

    const participantesFiltrados = useMemo(() => {
        return data.participantes.filter(participante => {
            const cumpleBusqueda =
                busquedaNombreCedula === "" ||
                participante.nombre.toLowerCase().includes(busquedaNombreCedula.toLowerCase()) ||
                participante.cedula.includes(busquedaNombreCedula);

            const cumpleMunicipio = filtroMunicipio === "todos" || participante.municipio === filtroMunicipio;
            const cumpleDepto = filtroDepto === "todos" || participante.departamento === filtroDepto;
            const cumpleProyecto = filtroProyecto === "todos" || participante.proyectos === filtroProyecto;

            return cumpleBusqueda && cumpleMunicipio && cumpleDepto && cumpleProyecto;
        });
    }, [data.participantes, busquedaNombreCedula, filtroMunicipio, filtroDepto, filtroProyecto]);

    const metricas = useMemo(() => {
        const totalParticipantes = participantesFiltrados.length;
        const participantesConArchivos = participantesFiltrados.filter(p => p.tiene_archivos).length;
        const participantesSinArchivos = totalParticipantes - participantesConArchivos;

        const totalArchivos = participantesFiltrados.reduce((sum, p) => {
            if (p.resultados && Object.keys(p.resultados).length > 0) {
                return sum + Object.values(p.resultados).reduce((pSum, proyecto) => {
                    return pSum + (proyecto.total || 0);
                }, 0);
            }
            return sum;
        }, 0);

        return { totalParticipantes, participantesConArchivos, participantesSinArchivos, totalArchivos };
    }, [participantesFiltrados]);


    const proyectosFiltrados = useMemo(() => {
        const proyectosMap = new Map()
        participantesFiltrados.forEach(p => {
            if (!proyectosMap.has(p.proyectos)) {
                proyectosMap.set(p.proyectos, {
                    nombre: p.proyectos,
                    totalParticipantes: 0,
                    participantesConArchivos: 0,
                    participantesSinArchivos: 0,
                })
            }
            const stats = proyectosMap.get(p.proyectos)
            stats.totalParticipantes++
            if (p.tiene_archivos) {
                stats.participantesConArchivos++
            } else {
                stats.participantesSinArchivos++
            }
        })
        return Array.from(proyectosMap.values())
    }, [participantesFiltrados])

    const datosProyectos = proyectosFiltrados.map(proyecto => ({
        nombre: proyecto.nombre,
        nombreCompleto: proyecto.nombre,
        conArchivos: proyecto.participantesConArchivos,
        sinArchivos: proyecto.participantesSinArchivos,
        total: proyecto.totalParticipantes,
    }))

    const datosDistribucion = [
        { name: "Con visitas realizadas", value: metricas.participantesConArchivos, color: "#10b981" },
        { name: "Sin visitas realizadas", value: metricas.participantesSinArchivos, color: "#ef4444" },
    ]

    const handleBarClick = data => {
        if (data && data.nombreCompleto) {
            setProyectoSeleccionado(data.nombreCompleto)
            setModalAbierto(true)
        }
    }

    const cerrarModal = () => {
        setModalAbierto(false)
        setProyectoSeleccionado(null)
    }

    const calcularDatosPorPeriodo = () => {
        const periodosMap = new Map()
        participantesFiltrados.forEach(p => {
            if (p.resultados && Object.keys(p.resultados).length > 0) {
                Object.values(p.resultados).forEach(proyecto => {
                    if (proyecto.subcarpetas) {
                        Object.entries(proyecto.subcarpetas).forEach(([periodo, cantidad]) => {
                            const periodoLimpio = periodo.replace(/^\d+\.\s*Visitas\s*/i, "")
                            if (!periodosMap.has(periodoLimpio)) {
                                periodosMap.set(periodoLimpio, 0)
                            }
                            periodosMap.set(periodoLimpio, periodosMap.get(periodoLimpio) + cantidad)
                        })
                    }
                })
            }
        })
        return periodosMap
    }

    const datosPorPeriodo = calcularDatosPorPeriodo()

    const datosRegistrosPorPeriodo = Array.from(datosPorPeriodo.entries()).map(([periodo, cantidad]) => ({
        periodo,
        registros: cantidad,
        fecha: parsePeriodoToDate(periodo),
    })).sort((a, b) => b.fecha - a.fecha)

    const { totalParticipantes, participantesConArchivos, participantesSinArchivos, totalArchivos } = metricas;
    const calcularPorcentaje = (parte, total) => total > 0 ? Math.round((parte / total) * 100) : 0;

    const datosConTotales = datosProyectos.map((item) => ({
        ...item,
        total: item.conArchivos + item.sinArchivos
    }));

    const datosOrdenados = useMemo(() => {
        return [...datosRegistrosPorPeriodo].sort((a, b) => {
            return parsePeriodoToDate(a.periodo) - parsePeriodoToDate(b.periodo);
        });
    }, [datosRegistrosPorPeriodo]);





    return (
        <div>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder="Buscar por Nombre o Cédula"
                        value={busquedaNombreCedula}
                        onChange={e => setBusquedaNombreCedula(e.target.value)}
                    />
                </Col>
                <Col span={6}>
                    <Select
                        style={{ width: "100%" }}
                        value={filtroMunicipio}
                        onChange={setFiltroMunicipio}
                        options={[{ value: "todos", label: "Todos los municipios" }, ...(data.municipios || []).map(m => ({ value: m, label: m }))]}
                    />
                </Col>
                <Col span={6}>
                    <Select
                        style={{ width: "100%" }}
                        value={filtroDepto}
                        onChange={setFiltroDepto}
                        options={[{ value: "todos", label: "Todos los departamentos" }, ...(data.departamentos || []).map(d => ({ value: d, label: d }))]}
                    />
                </Col>
                <Col span={6}>
                    <Select
                        style={{ width: "100%" }}
                        value={filtroProyecto}
                        onChange={setFiltroProyecto}
                        options={[{ value: "todos", label: "Todos los proyectos" }, ...(data.proyectosUnicos || []).map(p => ({ value: p, label: p }))]}
                    />
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 14, marginBottom: 14 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered hoverable style={{ height: 150 }}>
                        <div className={styles.cardContent}>
                            <UserOutlined className={styles.icon} />
                            <Title level={4}>Total Participantes</Title>
                            <p>{totalParticipantes}</p>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered hoverable style={{ height: 150 }}>
                        <div className={styles.cardContent}>
                            <FileDoneOutlined className={styles.icon} style={{ color: "#10b981" }} />
                            <Title level={4}>Participantes con al menos una visita</Title>
                            <p style={{ color: "#10b981" }}>
                                {participantesConArchivos} ({calcularPorcentaje(participantesConArchivos, totalParticipantes)}%)
                            </p>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Card bordered hoverable style={{ height: 150 }}>
                        <div className={styles.cardContent}>
                            <FileExclamationOutlined className={styles.icon} style={{ color: "#ef4444" }} />
                            <Title level={4}>Participantes sin visitas registradas</Title>
                            <p style={{ color: "#ef4444" }}>
                                {participantesSinArchivos} ({calcularPorcentaje(participantesSinArchivos, totalParticipantes)}%)
                            </p>
                        </div>
                    </Card>
                </Col>


                <Col xs={24} sm={12} md={6}>
                    <Card bordered hoverable style={{ height: 150 }}>
                        <div className={styles.cardContent}>
                            <FolderOpenOutlined className={styles.icon} />
                            <Title level={4}>Total Visitas</Title>
                            <p>{totalArchivos}</p>
                        </div>
                    </Card>
                </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} md={24}>
                    <Card
                        title={
                            <span>
                                Visitas de acompañamiento técnico por proyecto
                                <AntdTooltip
                                    title={
                                        <div>
                                            <p><strong>Proyectos:</strong> Cada barra representa un proyecto.</p>
                                            <p><strong>Con/Sin Visitas realizadas:</strong> Número de participantes con o sin archivos cargados.</p>
                                            <p><strong>Total:</strong> Etiqueta final con total por proyecto.</p>
                                        </div>
                                    }
                                >
                                    <InfoCircleOutlined style={{ color: "#10b981", marginLeft: 8 }} />
                                </AntdTooltip>
                            </span>
                        }
                    >
                        <ResponsiveContainer width="100%" height={40 * datosProyectos.length + 100}>
                            <BarChart
                                layout="vertical"
                                data={datosConTotales}
                                margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis dataKey="nombre" type="category" width={350} />
                                <Tooltip />
                                <Legend verticalAlign="top" height={36} />
                                <Bar
                                    dataKey="conArchivos"
                                    name="Con visitas realizadas"
                                    stackId="a"
                                    fill="#0869C6"
                                    onClick={handleBarClick}
                                    style={{ cursor: "pointer" }}
                                />
                                <Bar
                                    dataKey="sinArchivos"
                                    name="Sin visitas realizadas"
                                    stackId="a"
                                    fill="#b1afaf"
                                    onClick={handleBarClick}
                                    style={{ cursor: "pointer" }}
                                >
                                    <LabelList
                                        dataKey="total"
                                        position="right"
                                        style={{ fill: "#333", fontWeight: "bold" }}
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <Card
                        title={
                            <span>
                                Total Visitas por periodo  Proyectos FAO
                                <AntdTooltip
                                    title={
                                        <div>
                                            <p><strong>Período:</strong> Rango de tiempo agrupado (ej. meses).</p>
                                            <p><strong>Registros:</strong> Número total de acciones registradas en ese período.</p>
                                        </div>
                                    }
                                >
                                    <InfoCircleOutlined style={{ color: "#3b82f6", marginLeft: 8 }} />
                                </AntdTooltip>
                            </span>
                        }
                    >
                        <ResponsiveContainer width="100%" height={500}>
                            <BarChart
                                data={datosOrdenados}

                                margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="periodo"
                                    angle={-45}
                                    textAnchor="end"
                                    interval={0}
                                    height={80}
                                />
                                <YAxis
                                    label={{
                                        value: "Cantidad de Registros",
                                        angle: -90,
                                        position: "insideLeft",
                                        offset: 5,
                                        dy: 50
                                    }}
                                />
                                <Tooltip
                                    formatter={(value) => [value, "Registros"]}
                                    labelFormatter={(label) => `Período: ${label}`}
                                />
                                <Legend verticalAlign="top" height={36} />
                                <Bar
                                    dataKey="registros"
                                    fill="#3b82f6"
                                    name="Registros"
                                    radius={[4, 4, 0, 0]}
                                >
                                    <LabelList
                                        dataKey="registros"
                                        position="top"
                                        style={{ fill: "#333", fontWeight: "bold" }}
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card
                        title={
                            <span>
                                Distribución Total
                                <AntdTooltip
                                    title={
                                        <div>
                                            <p><strong>Con/Sin Visitas realizadas:</strong> Representa el porcentaje total en toda la muestra.</p>
                                        </div>
                                    }
                                >
                                    <InfoCircleOutlined style={{ color: "#8884d8", marginLeft: 8 }} />
                                </AntdTooltip>
                            </span>
                        }
                    >
                        <ResponsiveContainer width="100%" height={500}>
                            <PieChart>
                                <Pie
                                    data={datosDistribucion}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                                    outerRadius={90}
                                    dataKey="value"
                                >
                                    {datosDistribucion.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
            </Row>
            <div className="resumen-general">
                <Card title="Resumen General" style={{ marginTop: 16, marginBottom: 16 }}>
                    <div style={{ overflowX: "auto" }}>
                        <table
                            className="border border-gray-200"
                            style={{ minWidth: "1200px", tableLayout: "auto" }}
                        >
                            <thead style={{ backgroundColor: "#042956", color: "white" }}>
                                <tr className="border-b border-gray-200">
                                    <th
                                        className="text-center p-3 font-semibold border border-gray-200"
                                        style={{ whiteSpace: "nowrap" }}
                                    >
                                        Participantes
                                    </th>
                                    <th
                                        className="text-center p-3 font-semibold border border-gray-200"
                                        style={{ whiteSpace: "nowrap" }}
                                    >
                                        Con visitas realizadas
                                    </th>
                                    <th
                                        className="text-center p-3 font-semibold border border-gray-200"
                                        style={{ whiteSpace: "nowrap" }}
                                    >
                                        Sin visitas realizadas
                                    </th>
                                    {Array.from(datosPorPeriodo.keys())
                                        .sort((a, b) => parsePeriodoToDate(a) - parsePeriodoToDate(b))
                                        .map((periodo) => (
                                            <th
                                                key={periodo}
                                                className="text-center p-3 font-semibold border border-gray-200"
                                                style={{ whiteSpace: "nowrap" }}
                                            >
                                                {periodo}
                                            </th>
                                        ))}
                                    <th
                                        className="text-center p-3 font-semibold border border-gray-200"
                                        style={{ whiteSpace: "nowrap" }}
                                    >
                                        Total Registros
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b border-gray-200">
                                    <td
                                        className="text-center p-3 text-2xl font-bold border border-gray-200"
                                        style={{ whiteSpace: "nowrap" }}
                                    >
                                        {metricas.totalParticipantes}
                                    </td>
                                    <td
                                        className="text-center p-3 text-2xl font-bold border border-gray-200"
                                        style={{ color: "#10b981", whiteSpace: "nowrap" }}
                                    >
                                        {metricas.participantesConArchivos}
                                    </td>
                                    <td
                                        className="text-center p-3 text-2xl font-bold border border-gray-200"
                                        style={{ color: "#ef4444", whiteSpace: "nowrap" }}
                                    >
                                        {metricas.participantesSinArchivos}
                                    </td>
                                    {Array.from(datosPorPeriodo.keys())
                                        .sort((a, b) => parsePeriodoToDate(a) - parsePeriodoToDate(b))
                                        .map((periodo) => (
                                            <td
                                                key={periodo}
                                                className="text-center p-3 text-2xl font-bold border border-gray-200"
                                                style={{ whiteSpace: "nowrap" }}
                                            >
                                                {datosPorPeriodo.get(periodo) || 0}
                                            </td>
                                        ))}
                                    <td
                                        className="text-center p-3 text-2xl font-bold border border-gray-200"
                                        style={{ whiteSpace: "nowrap" }}
                                    >
                                        {metricas.totalArchivos}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
            <Modal
                open={modalAbierto}
                onCancel={cerrarModal}
                footer={null}
                width="90%"
                style={{ top: 20 }}
            >
                {proyectoSeleccionado && (
                    <DetalleProyectoModal
                        nombreProyecto={proyectoSeleccionado}
                        participantes={participantesFiltrados.filter(p => p.proyectos === proyectoSeleccionado)}
                        onClose={cerrarModal}
                    />
                )}
            </Modal>
        </div>
    )
}